import { Component, computed, effect, inject, input, output } from '@angular/core';
import { SensorBlockDetailDto, SensorBlockService } from '@artemis-software/wr-api';
import { Router } from '@angular/router';
import { NbDialogService, NbMenuItem, NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { CustomConfirmDialogComponent } from '@components/dialog/custom-confirm-dialog/custom-confirm-dialog.component';
import { SensorBlockDeleteDialogComponent } from '@components/dialog/sensor-block-delete-dialog/sensor-block-delete-dialog.component';
import { toRgba } from '@/utils/color-utils';
import { isCleanInFlight, isSyncInFlight } from '@/utils/sensor-status.util';

type SensorBlockAction = 'edit' | 'activate' | 'deactivate' | 'synchronize' | 'clean' | 'delete';

@Component({
  selector: 'wr-sensor-block-item',
  standalone: false,
  templateUrl: './sensor-block-item.component.html',
  styleUrl: './sensor-block-item.component.scss'
})
export class SensorBlockItemComponent {
  readonly sensorBlockItem = input.required<SensorBlockDetailDto>();
  readonly refreshed = output<void>();

  readonly router = inject(Router);
  readonly dialogService = inject(NbDialogService);
  readonly toastrService = inject(NbToastrService);
  readonly sensorBlockService = inject(SensorBlockService);

  getIcon(sensorBlock: SensorBlockDetailDto): string {
    return sensorBlock.icon?.presignedUrl ?? '';
  }

  async editSensorBlock(): Promise<void> {
    const sensorBlock = this.sensorBlockItem();
    await this.router.navigate(['sensorBlock', sensorBlock.id]);
  }

  async toggleSensorBlockActivation(value: boolean): Promise<void> {
    const sensorBlock = this.sensorBlockItem();
    const sensorBlockId = sensorBlock.id;
    if (sensorBlockId) {
      const text = value ? 'aktivieren' : 'deaktivieren';
      this.dialogService.open(CustomConfirmDialogComponent, {
        context: {
          title: `Sensoren ${text}`,
          message: `Möchten Sie wirklich alle Sensoren für dieses Gebäude ${text}?`,
        }
      }).onClose.subscribe(async (result: boolean) => {
        if (result) {
          await firstValueFrom(this.sensorBlockService.toggleActivationForSensorBlock(sensorBlockId, value));
          this.refreshed.emit();
        }
      });
    }
  }

  async triggerSensorBlockSynchronization(): Promise<void> {
    const sensorBlock = this.sensorBlockItem();
    const sensorBlockId = sensorBlock.id;
    if (sensorBlockId) {
      this.dialogService.open(CustomConfirmDialogComponent, {
        context: {
          title: 'Sensoren synchronisieren',
          message: 'Möchten Sie wirklich alle Sensoren für diesen Sensor Block synchronisieren?',
        }
      }).onClose.subscribe(async (result: boolean) => {
        if (result) {
          await firstValueFrom(this.sensorBlockService.triggerSensorBlockSynchronization(sensorBlockId)).catch(() => undefined);
          this.refreshed.emit();
        }
      });
    }
  }

  async triggerSensorBlockCleaning(): Promise<void> {
    const sensorBlock = this.sensorBlockItem();
    const sensorBlockId = sensorBlock.id;
    if (sensorBlockId) {
      this.dialogService.open(CustomConfirmDialogComponent, {
        context: {
          title: 'Sensordaten bereinigen',
          message: 'Möchten Sie wirklich die gesamten Sensordaten für diesen Sensor Block neu bereinigen? Dieser Vorgang kann einige Minuten dauern.',
        }
      }).onClose.subscribe(async (result: boolean) => {
        if (result) {
          await firstValueFrom(this.sensorBlockService.triggerSensorBlockCleaning(sensorBlockId)).catch(() => undefined);
          this.refreshed.emit();
        }
      });
    }
  }

  async deleteSensorBlock(): Promise<void> {
    const sensorBlock = this.sensorBlockItem();
    const sensorBlockId = sensorBlock.id;
    if (!sensorBlockId) return;
    this.dialogService.open(SensorBlockDeleteDialogComponent, {
      context: {
        sensorBlocks: [sensorBlock],
      }
    }).onClose.subscribe(async (result: boolean) => {
      if (!result) return;
      try {
        await firstValueFrom(this.sensorBlockService.deleteById(sensorBlockId));
        this.toastrService.success('Sensor Block wurde erfolgreich gelöscht', 'Sensor Block');
        this.refreshed.emit();
      } catch {
        this.toastrService.danger('Beim Löschen des Sensor Blocks ist ein Fehler aufgetreten', 'Sensor Block');
      }
    });
  }

  constructor() {
    effect(() => {
      const sensorBlockItem = this.sensorBlockItem();
      if (!sensorBlockItem) return;
    });
  }

  readonly syncInFlight = computed(() => this.sensorBlockItem().sensors.some((sensor) => isSyncInFlight(sensor)));
  readonly cleanInFlight = computed(() => this.sensorBlockItem().sensors.some((sensor) => isCleanInFlight(sensor)));

  readonly actions = computed<NbMenuItem[]>(() => [
    { title: 'Bearbeiten', data: 'edit' },
    { title: 'Aktivieren', data: 'activate' },
    { title: 'Deaktivieren', data: 'deactivate' },
    { title: 'Synchronisieren', data: 'synchronize', disabled: this.syncInFlight() },
    { title: 'Bereinigen', data: 'clean', disabled: this.cleanInFlight() },
    { title: 'Löschen', data: 'delete' },
  ]);

  onActionSelect(item: NbMenuItem) {
    switch (item.data as SensorBlockAction) {
      case 'edit': this.editSensorBlock(); break;
      case 'activate': this.toggleSensorBlockActivation(true); break;
      case 'deactivate': this.toggleSensorBlockActivation(false); break;
      case 'synchronize': this.triggerSensorBlockSynchronization(); break;
      case 'clean': this.triggerSensorBlockCleaning(); break;
      case 'delete': this.deleteSensorBlock(); break;
    }
  }

  protected readonly toRgba = toRgba;
}
